import { ReactElement, useCallback, useState } from 'react';
import { Modal } from '@components/common/modal/modal';
import {
	pauseSiteVisitRecording,
	resumeSiteVisitRecording,
	stopSiteVisitRecording,
} from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { useSiteVisitRecordingSessionState } from './useSiteVisitRecordingSessionState';
import { SiteVisitRecordingTimerDisplay } from './SiteVisitRecordingTimerDisplay';
import { SiteVisitRecordingStopConfirmModal } from './SiteVisitRecordingStopConfirmModal';

interface SiteVisitRecordingControlsPanelProps {
	readonly className?: string;
	/** Modal on Visit Recorded page uses centered timer and primary buttons. */
	readonly variant?: 'fab' | 'modal';
}

/** Shared timer + pause / resume / save controls for FAB and visit-recorded modal. */
export function SiteVisitRecordingControlsPanel({
	className,
	variant = 'fab',
}: Readonly<SiteVisitRecordingControlsPanelProps>): ReactElement {
	const { phase, displayTime, isSaving } = useSiteVisitRecordingSessionState();
	const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
	const isModal = variant === 'modal';

	const handleContinueRecording = useCallback((): void => {
		setStopConfirmOpen(false);
	}, []);

	const handleConfirmStop = useCallback((): void => {
		setStopConfirmOpen(false);
		stopSiteVisitRecording().catch(() => {});
	}, []);

	const pauseResumeButton =
		phase === 'recording' ? (
			<button
				type="button"
				className={isModal ? 'btn btn-primary btn-medium' : 'site-visit-recording-controls-btn'}
				onClick={() => {
					pauseSiteVisitRecording().catch(() => {});
				}}
				disabled={isSaving}
			>
				Pause
			</button>
		) : (
			phase === 'paused' && (
				<button
					type="button"
					className={isModal ? 'btn btn-primary btn-medium' : 'site-visit-recording-controls-btn'}
					onClick={() => {
						resumeSiteVisitRecording().catch(() => {});
					}}
					disabled={isSaving}
				>
					Resume
				</button>
			)
		);

	const saveStopButton = (
		<button
			type="button"
			className={
				isModal
					? 'btn btn-primary btn-medium'
					: 'site-visit-recording-controls-btn site-visit-recording-controls-btn-stop'
			}
			onClick={() => {
				if (isModal) {
					// BDE Feedback Save — stop immediately (no confirm).
					stopSiteVisitRecording().catch(() => {});
					return;
				}
				// Site Visit / Draft FAB Stop — confirm first.
				setStopConfirmOpen(true);
			}}
			disabled={isSaving}
		>
			{isSaving ? 'Saving...' : isModal ? 'Save' : 'Stop'}
		</button>
	);

	if (isModal) {
		return (
			<fieldset className={className} aria-label="BDE feedback recording controls">
				<div className="site-visit-recording-controls-modal-timer" aria-live="polite" aria-atomic="true">
					<SiteVisitRecordingTimerDisplay
						displayTime={displayTime}
						phase={phase}
						className="site-visit-recording-controls-time"
					/>
				</div>
				<div className="site-visit-recording-controls-modal-actions">
					{pauseResumeButton}
					{saveStopButton}
				</div>
			</fieldset>
		);
	}

	return (
		<>
			<fieldset className={className} aria-label="Site visit recording controls">
				<div aria-live="polite" aria-atomic="true" className="site-visit-recording-controls-fab-timer">
					<SiteVisitRecordingTimerDisplay displayTime={displayTime} phase={phase} />
				</div>
				<div className="site-visit-recording-controls-actions">
					{pauseResumeButton}
					{saveStopButton}
				</div>
			</fieldset>
			<Modal
				modalOpen={stopConfirmOpen}
				modalId="siteVisitRecordingStopConfirm"
				onModalClose={handleContinueRecording}
				className="dialog-sm site-lead-confrim"
				isEscAllow={false}
			>
				<SiteVisitRecordingStopConfirmModal onStop={handleConfirmStop} onContinue={handleContinueRecording} />
			</Modal>
		</>
	);
}
